import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "creditcost_estimate_web_v1";
const DEFAULT_RATE = 0.01; // $/coin default

const EstimateContext = createContext(null);
const makeId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const projectCoins = (p) => p.items.reduce((s, i) => s + (i.coins || 0), 0);

export function EstimateProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [rate, setRateState] = useState(DEFAULT_RATE);
  const [hydrated, setHydrated] = useState(false);

  // hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (Array.isArray(p.projects)) setProjects(p.projects);
        if (Array.isArray(p.purchases)) setPurchases(p.purchases);
        if (typeof p.rate === "number" && p.rate >= 0) setRateState(p.rate);
      }
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  // persist
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ projects, purchases, rate }));
  }, [projects, purchases, rate, hydrated]);

  const addProject = useCallback((name) => {
    const id = makeId();
    setProjects((prev) => [{ id, name: name.trim() || "Untitled Project", items: [] }, ...prev]);
    return id;
  }, []);

  const renameProject = useCallback((id, name) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === id ? { ...p, name: name.trim() || p.name } : p)),
    );
  }, []);

  const removeProject = useCallback((id) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const getProject = useCallback((id) => projects.find((p) => p.id === id), [projects]);

  const addItem = useCallback((projectId, item) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, items: [{ ...item, id: makeId() }, ...p.items] } : p,
      ),
    );
  }, []);

  const updateItem = useCallback((projectId, itemId, patch) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, items: p.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
          : p,
      ),
    );
  }, []);

  const removeItem = useCallback((projectId, itemId) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, items: p.items.filter((it) => it.id !== itemId) } : p,
      ),
    );
  }, []);

  const addPurchase = useCallback((pp) => {
    setPurchases((prev) => [{ ...pp, id: makeId() }, ...prev]);
  }, []);

  const updatePurchase = useCallback((id, patch) => {
    setPurchases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const removePurchase = useCallback((id) => {
    setPurchases((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const setRate = useCallback((r) => {
    setRateState(Number.isFinite(r) && r >= 0 ? r : 0);
  }, []);

  const clearProjects = useCallback(() => setProjects([]), []);
  const clearPurchases = useCallback(() => setPurchases([]), []);

  const coinsPurchased = useMemo(
    () => purchases.reduce((s, p) => s + (p.coins || 0), 0),
    [purchases],
  );
  const totalSpent = useMemo(
    () => purchases.reduce((s, p) => s + (p.dollars || 0), 0),
    [purchases],
  );
  const blendedRate = useMemo(
    () => (coinsPurchased > 0 ? totalSpent / coinsPurchased : rate),
    [coinsPurchased, totalSpent, rate],
  );
  const totalCoinsUsed = useMemo(
    () => projects.reduce((s, p) => s + projectCoins(p), 0),
    [projects],
  );
  const totalProjectCost = useMemo(
    () => totalCoinsUsed * blendedRate,
    [totalCoinsUsed, blendedRate],
  );
  const coinsRemaining = coinsPurchased - totalCoinsUsed;

  const value = {
    projects,
    purchases,
    rate,
    hydrated,
    coinsPurchased,
    totalSpent,
    blendedRate,
    totalCoinsUsed,
    totalProjectCost,
    coinsRemaining,
    addProject,
    renameProject,
    removeProject,
    getProject,
    addItem,
    updateItem,
    removeItem,
    addPurchase,
    updatePurchase,
    removePurchase,
    setRate,
    clearProjects,
    clearPurchases,
  };

  return <EstimateContext.Provider value={value}>{children}</EstimateContext.Provider>;
}

export function useEstimate() {
  const ctx = useContext(EstimateContext);
  if (!ctx) throw new Error("useEstimate must be used within EstimateProvider");
  return ctx;
}
